/**
 * applyUiDirective — core (B): the ui-action `report.*` / `html.*` switch.
 *
 * Lifted from the `event.type === 'ui-action'` branch of useChat.sendMessage's
 * SSE loop (useChat.ts L1132-L1899, pre-decomposition). The inline branch was a
 * single long `if (report.init) … else if (actions) … else if (report.status) …
 * else if (report.section) … else if (html.init) … else if (html.chunk) … else
 * if (html.status) … else if (html.complete) … else if (html.patch|insert|remove)`
 * chain. To keep each sibling under the ~400 LOC ceiling the chain is split into
 * two sub-appliers that preserve the EXACT else-if precedence:
 *
 *   1. {@link applyReportDirective} — report.init / actions / report.status /
 *      report.section. Returns true when it handled the action.
 *   2. {@link applyHtmlDirective} — html.init / html.chunk / html.status /
 *      html.complete / html.patch|insert|remove. Returns true when it handled it.
 *
 * Report sub-switch runs first (matching the inline ordering where report.* came
 * before html.*); only if it returns false do we try the html sub-switch. An
 * unrecognised action falls through both (no-op, exactly as the inline chain's
 * exhausted else-if would have).
 *
 * The AbortError thrown mid-create inside either sub-applier propagates out of
 * here unchanged so runChatStream's try/catch performs the same orphan cleanup
 * the inline sendMessage catch did.
 *
 * This function mutates the shared {@link TurnContext} BY REFERENCE — never
 * destructure or copy ctx fields. Pinned by useChat.ui-directive-char.test.ts +
 * useChat.html-stream-char.test.ts.
 */

import { applyReportDirective } from './applyReportDirective'
import { applyHtmlDirective } from './applyHtmlDirective'
import type { TurnContext, ChatStreamDeps } from './useChatStream.types'

type UiAction = { type: string; [key: string]: unknown }

export async function applyUiDirective(
  ctx: TurnContext,
  action: UiAction,
  deps: ChatStreamDeps,
  signal: AbortSignal,
): Promise<void> {
  // report.* / actions sub-switch first — preserves the inline else-if order.
  const handledByReport = await applyReportDirective(ctx, action, deps, signal)
  if (handledByReport) return
  // html.* sub-switch second.
  await applyHtmlDirective(ctx, action, deps, signal)
}
